import { useLoader, ThreeEvent } from '@react-three/fiber';
import React, { useContext, useEffect, useRef } from 'react';
import AssetLoader from '../AssetLoader';
import ThreekitSourceContext from '../ThreekitSourceContext';
import { ThreekitApiContext } from '../ThreekitApi';
import {
  ActionState,
  AssetState,
  AttributeStateMap,
  ConfigurationType,
  EvaluatedAsset,
  SceneGraphNode,
  ThreekitApiData,
  ThreekitSource,
} from '../../types';
import evaluateAsset from '../../sceneGraph/evaluateAsset';
import applyConfiguration from '../../sceneGraph/applyConfiguration';

interface EvaluatedCache {
  evaluatedAsset: EvaluatedAsset | false;
  attributeStateMap: AttributeStateMap;
}
const evalCache: Map<SceneGraphNode, EvaluatedCache> = new Map();

// Evaluate the asset if required, and return EvaluatedAsset.
//
// This should only evaluate when the `configurator` changes, rather than the
// node itself. This might have an effect for Materials, and Images, where the
// root node is also the node with data, unlike say Models, where we have a
// container null at the root.
export function cachedEvaluateAsset(
  state: AssetState,
  source: ThreekitSource,
  resolveAttributes: boolean
) {
  const node = state.nodes[state.id];
  let result = evalCache.get(node);
  // console.log('cached evaluated asset', node.name, result);
  if (result !== undefined) return result;
  result = evaluateAsset(node, source, resolveAttributes);
  evalCache.set(node, result);
  return result;
}

function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value; //assign the value of ref to the argument
  }, [value]); //this code will run when the value of 'value' changes
  return ref.current; //in the end, return the current ref value.
}

// function useConfigurator(idOrKey: string) {
//   const { source } = useContext(ThreekitSourceContext);
// 	const ref = source.getRef(idOrKey);
// 	function setConfiguration(conf: ConfigurationType) {

// 	}
// 	return [ref.current.attributes, setConfiguration]
// }

// useAsset -- take an asset id, initial configuration, and potential 'key' ref
// and return a 'ref', which contains the asset

export interface useAssetProps {
  id: string;
  refkey: string;
  configuration?: ConfigurationType;
}

export default function useAsset(
  props: useAssetProps
): [AssetState, (state: AssetState) => void] {
  const { refkey } = props;
  const apiContext = useContext(ThreekitApiContext);

  const result = useAssetInternal({ ...props });

  if (refkey) {
    // console.log('setAssetRef', refkey, result);
    apiContext.setAssetRef(refkey, result);
  }

  function setState(inputAssetState: AssetState) {
    apiContext.setAssetRef(refkey, { inputAssetState });
  }
  // console.log('useAsset', id, result);
  return [result.appliedAssetState, setState]; // [assetState, configuration, attributes];
}

interface useAssetInternalProps {
  id?: string;
  refkey?: string;
  configuration?: ConfigurationType;
  assetState?: AssetState;
}

export function useAssetInternal(
  props: useAssetInternalProps
): ThreekitApiData {
  const { source } = useContext(ThreekitSourceContext);
  const apiContext = useContext(ThreekitApiContext);
  const { refkey } = props;

  const inputData = refkey ? apiContext.getAssetRef(refkey) : null;

  let loadState =
    !inputData?.inputAssetState &&
    useLoader<AssetState, any>(
      AssetLoader,
      props.id,
      AssetLoader.attach(source) //, !props.fromId && !!props.useManifest) // If no `fromId` provided, fetch with manifest
    );

  // `useLoader` incorrectly types the return as possibly being an array. hack fix for type error
  if (loadState && Array.isArray(loadState)) loadState = loadState[0];

  const inputAssetState = inputData?.inputAssetState ?? loadState;
  const configuration =
    inputData?.inputConfiguration ?? props.configuration ?? {};

  // console.log('useAsset, configuration', inputAssetState, configuration);

  if (!inputAssetState) throw new Error('Invalid input state');

  const { evaluatedAsset, attributeStateMap } = cachedEvaluateAsset(
    inputAssetState,
    source,
    true // resolve attribute values
  );

  // const prevConfiguration = usePrevious(inputConf); // use this hook for 'changed' conditions

  // given input state, apply new configuration (cached by config)
  return evaluatedAsset
    ? applyConfiguration(
        inputAssetState,
        evaluatedAsset,
        attributeStateMap,
        configuration
      )
    : {
        inputAssetState,
        appliedAssetState: inputAssetState,
        attributeStateMap: {},
        inputConfiguration: {},
        appliedConfiguration: {},
        evaluatedAsset: false,
      };
}
